import React, { useEffect, useState, useMemo } from 'react';
import * as Yup from 'yup';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Plus, X, HelpCircle } from 'lucide-react';
import CustomModal from '../../../../components/CustomModal/CustomModal';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import CustomTooltip from '../../../../components/CustomTooltip/CustomTooltip';
import Button from '../../../../components/Button/Button';
import SearchableDropdown, {
  DropdownItem
} from '../../../../components/SearchableDropdown/SearchableDropdown';
import EnumeratedSlotValue from '../../../../types/BotBuilder/EnumeratedSlotValue';
import SlotValueType, {
  SlotValueTypeLabels
} from '../../../../types/BotBuilder/SlotValueType';

interface FormValues {
  slotName: string;
  valueType: SlotValueType;
  enumeratedSlotValues: EnumeratedSlotValue[];
}

interface CreateSlotModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: FormValues) => Promise<void>;
  defaultValues?: FormValues;
}

function CreateSlotModal({
  open,
  onClose,
  onSubmit,
  defaultValues
}: Readonly<CreateSlotModalProps>): React.ReactElement {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedValueIndex, setSelectedValueIndex] = useState<number>(-1);
  const [searchQuery, setSearchQuery] = useState('');

  const validationSchema = Yup.object().shape({
    slotName: Yup.string().required('Variable name is required'),
    valueType: Yup.string()
      .oneOf(
        [
          SlotValueType.ENUMERATED_VALUE,
          SlotValueType.NUMBER,
          SlotValueType.FREE_TEXT,
          SlotValueType.DATE,
          SlotValueType.DOCUMENT_UPLOAD
        ],
        'Invalid value type'
      )
      .required('Value type is required'),
    enumeratedSlotValues: Yup.array().when('valueType', {
      is: SlotValueType.ENUMERATED_VALUE,
      then: Yup.array()
        .of(
          Yup.object().shape({
            resolvedValue: Yup.string().required('Resolved value is required'),
            synonyms: Yup.array().of(
              Yup.object().shape({
                synonym: Yup.string().trim().required('Synonym cannot be empty')
              })
            )
          })
        )
        .min(1, 'At least one variable value is required')
        .test(
          'unique-resolved-values',
          'Duplicate resolved values are not allowed',
          (values) => {
            if (!values) return true;
            const resolvedValues = values
              .map((v) => v.resolvedValue?.toLowerCase().trim())
              .filter(Boolean);
            return resolvedValues.length === new Set(resolvedValues).size;
          }
        ),
      otherwise: Yup.array().notRequired()
    })
  });

  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValues || {
      slotName: '',
      valueType: SlotValueType.ENUMERATED_VALUE,
      enumeratedSlotValues: []
    }
  });

  const {
    fields: slotFields,
    append: appendSlot,
    remove: removeSlot
  } = useFieldArray({
    control,
    name: 'enumeratedSlotValues'
  });

  const currentValueType = watch('valueType');
  const enumeratedSlotValues = watch('enumeratedSlotValues');

  const formatSynonymCount = (synonymsLength: number): string => {
    return `${synonymsLength} synonym${synonymsLength === 1 ? '' : 's'}`;
  };

  const dropdownItems = useMemo((): DropdownItem[] => {
    const items: DropdownItem[] = [];

    enumeratedSlotValues?.forEach((value: any, index: number) => {
      items.push({
        id: index,
        label: value.resolvedValue || '(Empty value)',
        sublabel: value.synonyms?.length
          ? formatSynonymCount(value.synonyms.length)
          : undefined
      });
    });

    return items;
  }, [enumeratedSlotValues]);

  const filteredItems = useMemo((): DropdownItem[] => {
    if (!searchQuery.trim()) {
      return dropdownItems;
    }

    const query = searchQuery.toLowerCase().trim();
    return dropdownItems.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.sublabel?.toLowerCase().includes(query)
    );
  }, [dropdownItems, searchQuery]);

  const selectedValueItem = useMemo((): DropdownItem | null => {
    if (
      selectedValueIndex < 0 ||
      selectedValueIndex >= (enumeratedSlotValues?.length || 0)
    ) {
      return null;
    }

    const currentValue = enumeratedSlotValues?.[selectedValueIndex];
    if (!currentValue) return null;

    return {
      id: selectedValueIndex,
      label: currentValue.resolvedValue || '(Empty value)',
      sublabel: currentValue.synonyms?.length
        ? formatSynonymCount(currentValue.synonyms.length)
        : undefined
    };
  }, [selectedValueIndex, enumeratedSlotValues]);

  useEffect(() => {
    if (open) {
      const initialValues = defaultValues || {
        slotName: '',
        valueType: SlotValueType.ENUMERATED_VALUE,
        enumeratedSlotValues: []
      };

      reset(initialValues);

      if (
        initialValues.enumeratedSlotValues &&
        initialValues.enumeratedSlotValues.length > 0
      ) {
        setSelectedValueIndex(0);
      } else {
        setSelectedValueIndex(-1);
      }

      setSearchQuery('');
    } else {
      setErrorMessage('');
      setSearchQuery('');
    }
  }, [open, reset, setValue, defaultValues]);

  const handleValueSelection = (item: DropdownItem): void => {
    const newIndex = item.id as number;
    setSelectedValueIndex(newIndex);
  };

  const handleAddNewValue = (): void => {
    const currentLength = enumeratedSlotValues?.length || 0;
    appendSlot({
      id: null,
      resolvedValue: '',
      synonyms: []
    });
    setSelectedValueIndex(currentLength);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSearch = (query: string, _page: number): void => {
    if (query !== searchQuery) {
      setSearchQuery(query);
    }
  };

  const valueTypeOptions = [
    {
      value: SlotValueType.ENUMERATED_VALUE,
      label: SlotValueTypeLabels[SlotValueType.ENUMERATED_VALUE]
    },
    {
      value: SlotValueType.NUMBER,
      label: SlotValueTypeLabels[SlotValueType.NUMBER]
    },
    {
      value: SlotValueType.FREE_TEXT,
      label: SlotValueTypeLabels[SlotValueType.FREE_TEXT]
    },
    {
      value: SlotValueType.DATE,
      label: SlotValueTypeLabels[SlotValueType.DATE]
    },
    {
      value: SlotValueType.DOCUMENT_UPLOAD,
      label: SlotValueTypeLabels[SlotValueType.DOCUMENT_UPLOAD]
    }
  ];

  const handleRemoveSlot = (indexToRemove: number): void => {
    removeSlot(indexToRemove);

    if (selectedValueIndex === indexToRemove) {
      const newIndex = Math.max(0, indexToRemove - 1);
      setSelectedValueIndex(slotFields.length <= 1 ? -1 : newIndex);
    } else if (selectedValueIndex > indexToRemove) {
      setSelectedValueIndex(selectedValueIndex - 1);
    }
  };

  const addSynonym = (slotIndex: number): void => {
    const currentSynonyms = enumeratedSlotValues[slotIndex]?.synonyms || [];
    setValue(`enumeratedSlotValues.${slotIndex}.synonyms`, [
      ...currentSynonyms,
      { id: null, synonym: '' }
    ]);
  };

  const removeSynonym = (slotIndex: number, synonymIndex: number): void => {
    const currentSynonyms = enumeratedSlotValues[slotIndex]?.synonyms || [];
    const updatedSynonyms = currentSynonyms.filter(
      (_: any, i: number) => i !== synonymIndex
    );
    setValue(`enumeratedSlotValues.${slotIndex}.synonyms`, updatedSynonyms);
  };

  const onFormSubmit = async (values: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onSubmit(values);
      onClose();
    } catch (error: any) {
      const message =
        error.response?.data?.message ??
        error.message ??
        'Something went wrong.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    const initialValues = defaultValues || {
      slotName: '',
      valueType: SlotValueType.ENUMERATED_VALUE,
      enumeratedSlotValues: []
    };
    reset(initialValues);
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title='Create global variable'
      maxWidth='xl'
    >
      <form onSubmit={handleSubmit(onFormSubmit)} className='space-y-6'>
        <div className='flex items-start gap-3'>
          <div className='flex-1'>
            <CustomInput
              label='Variable name'
              placeholder='Enter variable name'
              containerClassName='mb-0 w-full'
              className='bg-white'
              error={errors.slotName}
              {...register('slotName')}
            />
          </div>
          <CustomTooltip
            content='The name for the variable in your chatbot'
            side='right'
          >
            <div className='flex items-center justify-center mt-8'>
              <HelpCircle className='w-4 h-4 cursor-help' />
            </div>
          </CustomTooltip>
        </div>

        <div className='flex items-start gap-3'>
          <div className='flex-1'>
            <div className='[&>div]:mb-0 [&>label]:text-white [&>label]:font-medium'>
              <CustomSelect
                label='Value type'
                value={currentValueType}
                onValueChange={(value) =>
                  setValue('valueType', value as SlotValueType)
                }
                options={valueTypeOptions}
                className='bg-white'
                fullWidth
              />
            </div>
          </div>
          <CustomTooltip
            content='The type of value captured by this variable'
            side='right'
          >
            <div className='flex items-center justify-center mt-8'>
              <HelpCircle className='w-4 h-4 cursor-help' />
            </div>
          </CustomTooltip>
        </div>

        {currentValueType === SlotValueType.ENUMERATED_VALUE && (
          <div className='space-y-4'>
            <div className='space-y-2'>
              <div className='flex gap-2'>
                <div className='flex-1'>
                  <SearchableDropdown
                    items={filteredItems}
                    selectedItem={selectedValueItem}
                    onSelect={handleValueSelection}
                    onSearch={handleSearch}
                    placeholder='Select a value to edit...'
                    searchPlaceholder='Search values...'
                    className='w-full'
                    triggerClassName='bg-white text-black border-gray-300'
                  />
                </div>
                <Button
                  type='button'
                  variant='outline'
                  onClick={handleAddNewValue}
                  icon={<Plus size='16' />}
                  className='flex-shrink-0'
                >
                  Add value
                </Button>
              </div>
            </div>

            {selectedValueIndex >= 0 &&
              selectedValueIndex < slotFields.length && (
                <div className='border border-gray-300 rounded-lg p-4 space-y-4 bg-white'>
                  <div className='flex items-start gap-3'>
                    <div className='flex-1'>
                      <CustomInput
                        label='Resolved value'
                        placeholder='E.g. phone'
                        containerClassName='mb-0 w-full'
                        className='bg-white'
                        error={
                          errors.enumeratedSlotValues?.[selectedValueIndex]
                            ?.resolvedValue
                        }
                        {...register(
                          `enumeratedSlotValues.${selectedValueIndex}.resolvedValue` as const
                        )}
                      />
                    </div>
                    <CustomTooltip
                      content='This is the main value that you want to capture, e.g. phone'
                      side='right'
                    >
                      <div className='flex items-center justify-center mt-8'>
                        <HelpCircle className='w-4 h-4 cursor-help' />
                      </div>
                    </CustomTooltip>
                  </div>

                  <div className='space-y-2'>
                    <div className='flex items-center gap-3'>
                      <h5 className='text-sm font-medium text-gray-900'>
                        Synonyms
                      </h5>
                      <CustomTooltip
                        content='Synonyms for the value that you want to capture; e.g. mobile, telephone, cellphone'
                        side='right'
                      >
                        <HelpCircle className='w-4 h-4 ml-2 cursor-help' />
                      </CustomTooltip>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => addSynonym(selectedValueIndex)}
                        icon={<Plus size='14' />}
                        className='ml-auto text-gray-400 hover:text-gray-300'
                      >
                        Add synonym
                      </Button>
                    </div>

                    <div className='max-h-36 overflow-y-auto space-y-2 py-2'>
                      {enumeratedSlotValues?.[
                        selectedValueIndex
                      ]?.synonyms?.map(
                        (_synonym: any, synonymIndex: number) => (
                          <div
                            key={`synonym-${selectedValueIndex}-${synonymIndex}`}
                            className='flex items-center gap-2'
                          >
                            <CustomInput
                              placeholder={`Synonym ${synonymIndex + 1}`}
                              containerClassName='mb-0 flex-1'
                              className='bg-white ml-1'
                              error={
                                errors.enumeratedSlotValues?.[
                                  selectedValueIndex
                                ]?.synonyms?.[synonymIndex]?.synonym
                              }
                              {...register(
                                `enumeratedSlotValues.${selectedValueIndex}.synonyms.${synonymIndex}.synonym` as const
                              )}
                            />
                            <Button
                              type='button'
                              variant='ghost'
                              size='sm'
                              onClick={() =>
                                removeSynonym(selectedValueIndex, synonymIndex)
                              }
                              className='text-red-400 hover:text-red-300 px-4'
                            >
                              <X size='14' />
                            </Button>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  <div className='pt-4 border-t border-gray-200'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => handleRemoveSlot(selectedValueIndex)}
                      className='w-full bg-red-50 border-red-300 text-red-600 hover:bg-red-100 hover:border-red-400'
                    >
                      Remove value
                    </Button>
                  </div>
                </div>
              )}

            {slotFields.length === 0 && (
              <div className='text-center py-8 border-2 border-dashed border-gray-300 rounded-lg'>
                <p className='text-sm text-gray-500 mb-4'>
                  No variable values created yet
                </p>
              </div>
            )}

            {errors.enumeratedSlotValues?.message && (
              <p className='text-sm text-red-500'>
                {errors.enumeratedSlotValues.message}
              </p>
            )}
          </div>
        )}

        {errorMessage && (
          <div className='bg-red-900/20 border border-red-500 rounded-lg p-3'>
            <p className='text-sm text-red-400'>{errorMessage}</p>
          </div>
        )}

        <div className='flex gap-3 justify-end pt-4'>
          <Button
            type='button'
            variant='ghost'
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type='submit' loading={isSubmitting}>
            {defaultValues ? 'Update variable' : 'Create variable'}
          </Button>
        </div>
      </form>
    </CustomModal>
  );
}

export default CreateSlotModal;
